/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Link,
  Breadcrumbs,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  createGlossaryFromParsedFile,
  updateGlossaryFromParsedFile,
  glossaryActions,
  GlossaryItemTemplateDto,
  ImportFileParseStatus,
  importExternalGlossary,
  importInternalGlossary,
} from '../../reducers/glossary';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { getStore, RootState } from '@ink-ai/portal/reducers';
import {
  glossaryJsonTemplate,
  glossaryCSVTemplate,
} from '../components/glossary/GlossaryTemplate';
import LoadingButton from '@mui/lab/LoadingButton';
import { hasAnyRole, superAdminRolesForEnv, isDefaultProtectedName } from '@ink-ai/portal/common/authHelper';
import { app } from '@ink-ai/portal/reducers/app';
import Papa from 'papaparse';
import { useNavigationListener } from '../components/hooks/useNavigationListener';
import {
  downloadFile,
  formatFileSize,
  glossaryJsonValidate,
} from '@ink-ai/portal/common/utils';
import { EllipsisCell } from '@ink-ai/portal/common/components/paginated-table/EllipsisCell';
import { PaginatedTable } from '@ink-ai/portal/common/components/paginated-table/PaginatedTable';
import {
  GlossaryCollectionDtoTypeEnum,
  Language,
} from '@ink-ai/insight-service-sdk';
import { PaperCard } from '../components/Common';

const externalGlossarySizeLimit = 10 * 1024 * 1024;
const externalGlossaryItemCountLimit = -1;

const downloadJsonTemplate = () => {
  downloadFile(
    JSON.stringify(glossaryJsonTemplate, null, 2),
    'glossary-template.json',
    'application/json',
  );
};

const downloadCSVTemplate = () => {
  downloadFile(
    `\uFEFF${glossaryCSVTemplate}`, // Using BOM to force Excel to open CSV in UTF-8
    'glossary-template.csv',
    'text/csv',
  );
};

interface ImportGlossaryProps {
  glossaryUuid?: string;
  glossaryName?: string;
  glossaryDescription?: string;
  glossaryType?: GlossaryCollectionDtoTypeEnum;
  extractedFile?: File;
  extractedFileName?: string;
}

interface CsvItem {
  name: string;
  desc: string;
  abbr: string;
  ZH_CN: string;
  EN_US: string;
  JA: string;
}

export const ImportGlossary: React.FC<ImportGlossaryProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const store = getStore();
  const location = useLocation();
  const { 
    glossaryUuid, 
    glossaryName, 
    glossaryDescription, 
    glossaryType,
    extractedFile,
    extractedFileName
  } = location.state || {};
  const navigate = useNavigate();
  const glossary = useSelector((state: RootState) => state.glossary);
  const auth = useSelector((state: RootState) => state.auth);
  const isSuperAdmin = hasAnyRole(auth.group, superAdminRolesForEnv());
  const [name, setName] = useState(glossaryName);
  // Only super-admins may create/edit a "default"-named glossary. Block it at
  // the name field so the user finds out immediately (the server also enforces
  // this with a 403).
  const nameIsProtected = !isSuperAdmin && isDefaultProtectedName(name);
  const [description, setDescription] = useState(glossaryDescription);
  const [fileName, setFileName] = useState(extractedFileName || '');
  const [fileSize, setFileSize] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useNavigationListener();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      glossary.importFileParseStatus === ImportFileParseStatus.Uploading &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (extractedFile) {
      handleFileUpload(extractedFile);
    }
  }, [extractedFile]);

  const columns = [
    {
      id: 'name',
      label: t('glossary.table.name'),
      render: (item: GlossaryItemTemplateDto) => (
        <EllipsisCell content={item.name} maxLines={3} />
      ),
      width: 150,
    },
    {
      id: 'description',
      label: t('glossary.table.description'),
      render: (item: GlossaryItemTemplateDto) => (
        <EllipsisCell content={item.desc || '-'} maxLines={3} />
      ),
      width: 150,
    },
    {
      id: 'abbreviation',
      label: t('glossary.table.abbreviation'),
      render: (item: GlossaryItemTemplateDto) => (
        <EllipsisCell content={item.abbr || '-'} />
      ),
      width: 150,
    },
    {
      id: 'translations',
      label: t('glossary.table.translations'),
      render: (item: GlossaryItemTemplateDto) => (
        <Box>
          {Object.entries(item.translations).map(([key, value]) => (
            <div key={key}>
              {value.language}: {value.translation}
            </div>
          ))}
        </Box>
      ),
      width: 150,
    },
  ];

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    setFileName(file.name);
    setFileSize(file.size);
    setSelectedFile(file);
    setIsParsing(true);

    // 检查文件大小限制
    if (file.size > externalGlossarySizeLimit) {
      console.log(
        'File size exceeds the internal db limit, use external db for large files',
      );
      // 文件太大，强制使用external db，跳过解析
      setItemCount(0);
      dispatch(
        glossaryActions.setImportFileParseStatus({
          status: ImportFileParseStatus.Complete,
          content: null,
        }),
      );
      setIsParsing(false);
      return;
    }

    // 如果明确指定为External类型，只允许CSV文件
    if (glossaryType === GlossaryCollectionDtoTypeEnum.External) {
      if (!file.name.endsWith('.csv')) {
        store.dispatch(
          app.actions.setGlobalMessage({
            message: 'Only CSV files are allowed for external glossaries',
            status: 'error',
          }),
        );
        handleFileRemove();
        setIsParsing(false);
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        try {
          const fileContent = e.target.result as string;
          if (file.name.endsWith('.json')) {
            handleJsonParse(fileContent);
          } else if (file.name.endsWith('.csv')) {
            handleCsvParse(fileContent);
          } else {
            throw new Error('Unsupported file format');
          }
        } catch (error) {
          console.log('error: ', error);
          store.dispatch(
            app.actions.setGlobalMessage({
              message:
                'Failed to parse the file. Please ensure it is a valid JSON or CSV.',
              status: 'error',
            }),
          );
          handleFileRemove();
        } finally {
          setIsParsing(false);
        }
      }
    };
    reader.onerror = () => {
      store.dispatch(
        app.actions.setGlobalMessage({
          message: 'Failed to read the file. Please try again.',
          status: 'error',
        }),
      );
      handleFileRemove();
    };
    reader.readAsText(file);
  };

  const handleJsonParse = (fileContent: string) => {
    const content = JSON.parse(fileContent);
    if (glossaryJsonValidate(content)) {
      setItemCount(content.length);
      
      // 根据项目数量决定是否使用external db
      const shouldUseExternalDb = content.length > externalGlossaryItemCountLimit;
      
      if (shouldUseExternalDb) {
        console.log(
          `Number of items (${content.length}) exceeds ${externalGlossaryItemCountLimit}, will use external db`,
        );
        // 项目数量超过限制，使用external db，不显示预览
        dispatch(
          glossaryActions.setImportFileParseStatus({
            status: ImportFileParseStatus.Complete,
            content: null, // 不保存内容到state，因为会使用external db
          }),
        );
      } else {
        console.log(
          `Number of items (${content.length}) within limit, will use internal db`,
        );
        // 项目数量在限制内，使用internal db，显示预览
        dispatch(
          glossaryActions.setImportFileParseStatus({
            status: ImportFileParseStatus.Complete,
            content,
          }),
        );
      }
    } else {
      setItemCount(0);
      console.log('Validation errors: ', glossaryJsonValidate.errors);
      throw new Error('Invalid JSON format');
    }
  };

  const handleCsvParse = (fileContent: string) => {
    Papa.parse<CsvItem>(fileContent, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const content = result.data.map((item) => {
          const translations = Object.values(Language).reduce(
            (acc, lang) => {
              if (item[lang]) {
                acc.push({ language: lang, translation: item[lang] });
              }
              return acc;
            },
            [] as Array<{ language: string; translation: string }>,
          );

          return {
            name: item.name,
            desc: item.desc,
            abbr: item.abbr,
            translations,
          };
        });
        setItemCount(content.length);
        
        // 根据项目数量决定是否使用external db
        const shouldUseExternalDb = content.length > externalGlossaryItemCountLimit;
        
        if (shouldUseExternalDb) {
          console.log(
            `Number of items (${content.length}) exceeds ${externalGlossaryItemCountLimit}, will use external db`,
          );
          // 项目数量超过限制，使用external db，不显示预览
          setItemCount(0); // 重置itemCount为0，这样showUploadBox会返回true
          dispatch(
            glossaryActions.setImportFileParseStatus({
              status: ImportFileParseStatus.Complete,
              content: null, // 不保存内容到state，因为会使用external db
            }),
          );
        } else {
          console.log(
            `Number of items (${content.length}) within limit, will use internal db`,
          );
          // 项目数量在限制内，使用internal db，显示预览
          dispatch(
            glossaryActions.setImportFileParseStatus({
              status: ImportFileParseStatus.Complete,
              content: content as GlossaryItemTemplateDto[],
            }),
          );
        }
      },
      error: () => {
        store.dispatch(
          app.actions.setGlobalMessage({
            message:
              'Failed to parse the CSV file. Please ensure it is a valid CSV.',
            status: 'error',
          }),
        );
        handleFileRemove();
        setItemCount(0);
      },
    });
  };

  const handleImportGlossary = async (isUseExternalDb: boolean) => {
    dispatch(glossaryActions.setLoading(true));
    try {
      if (glossaryUuid) {
        if (isUseExternalDb) {
          await dispatch(
            importExternalGlossary({
              uuid: glossaryUuid,
              name,
              description,
              file: selectedFile as File,
            }),
          ).unwrap();
        } else {
          // 内部数据库模式，使用 S3 上传
          await dispatch(
            importInternalGlossary({
              uuid: glossaryUuid,
              name,
              description,
              file: selectedFile as File,
            }),
          ).unwrap();
        }
      } else {
        if (isUseExternalDb) {
          await dispatch(
            importExternalGlossary({ name, description, file: selectedFile as File }),
          ).unwrap();
        } else {
          // 内部数据库模式，使用 S3 上传
          await dispatch(
            importInternalGlossary({ name, description, file: selectedFile as File }),
          ).unwrap();
        }
      }
    } catch (error: any) {
      const status = error?.response?.status;
      console.log(error)
      const backendMessage = error?.response?.data?.message;
      const messageToShow =
        status === 400 && backendMessage
          ? backendMessage
          : error?.message || t('glossary.import.error');

      store.dispatch(
        app.actions.setGlobalMessage({
          message: messageToShow,
          status: 'error',
        }),
      );
      // 取消 loading 并恢复到非上传状态，避免继续上传
      dispatch(glossaryActions.setLoading(false));
      dispatch(
        glossaryActions.setImportFileParseStatus({
          status: ImportFileParseStatus.WaitingUpload,
          content: glossary.parsedFileContent,
        }),
      );
    }
  };

  const handleFileRemove = () => {
    setFileName('');
    setFileSize(0);
    dispatch(
      glossaryActions.setImportFileParseStatus({
        status: ImportFileParseStatus.WaitingUpload,
        content: null,
      }),
    );
    setSelectedFile(null);
    dispatch(glossaryActions.setLoading(false));
    if (fileInputRef.current) {
      try { fileInputRef.current.value = ''; } catch {}
    }
  };

  const handleCancel = () => {
    navigate('/glossary-management');
  };

  useEffect(() => {
    // Clean up function to reset glossary state when component unmounts
    return () => {
      handleFileRemove();
    };
  }, []);

  const parsedItems = glossary.parsedFileContent || [];

  const isFileTooLarge = fileSize > externalGlossarySizeLimit;
  const isItemCountExceeded = itemCount > externalGlossaryItemCountLimit;
  
  // 动态决定是否使用external db
  // 1. 如果明确指定为External类型，使用external db
  // 2. 如果文件太大，使用external db
  // 3. 如果项目数量超过限制，使用external db
  // 4. 如果明确指定为Internal类型，使用internal db
  const isUseExternalDb =
    glossaryType === GlossaryCollectionDtoTypeEnum.External ||
    (isFileTooLarge && glossaryType !== GlossaryCollectionDtoTypeEnum.Internal) ||
    (isItemCountExceeded && glossaryType !== GlossaryCollectionDtoTypeEnum.Internal);

  const showUploadBox = () => {
    // 如果正在解析，显示加载状态
    if (isParsing) {
      return false;
    }
    
    // 如果已经有文件且解析完成，显示文件名（无论是internal还是external）
    if (fileName && (itemCount > 0 || isFileTooLarge || isItemCountExceeded)) {
      return false;
    }
    
    // 其他情况显示上传框
    return true;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          color="inherit"
          onClick={() => navigate('/glossary-management')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('glossary.navigation.glossaryManagement')}
        </Link>
        <Typography color="textPrimary">
          {t('glossary.import.navigation.import')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left' }}>
          {glossaryUuid
            ? t('glossary.import.title.update')
            : t('glossary.import.title.import')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'left' }}
        >
          {glossaryUuid
            ? t('glossary.import.description.update')
            : t('glossary.import.description.import')}
        </Typography>
        <Box sx={{ width: '50%' }}>
          <TextField
            label={t('glossary.import.form.name')}
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={name}
            error={nameIsProtected}
            helperText={nameIsProtected ? t('glossary.import.form.defaultNameForbidden') : undefined}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label={t('glossary.import.form.description')}
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
            value={description}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}>
            {t('glossary.import.template.download')}{' '}
            {/* {glossaryType !== GlossaryCollectionDtoTypeEnum.External ? (
              <>
                <Link href="#" onClick={downloadJsonTemplate}>
                  {t('glossary.import.template.json')}
                </Link>
                {' ' + t('glossary.import.template.and') + ' '}
              </>
            ) : null} */}
            <Link href="#" onClick={downloadCSVTemplate}>
              {t('glossary.import.template.csv')}
            </Link>
            {', ' + t('glossary.import.template.import')}.
          </Typography>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '1px dashed grey',
              p: 2,
              mb: 2,
              textAlign: 'center',
              borderRadius: '4px',
              borderColor: 'grey.400',
            }}
          >
            <input
              ref={fileInputRef}
              accept={
                glossaryType === GlossaryCollectionDtoTypeEnum.External
                  ? '.csv'
                  : '.json, .csv'
              }
              className="hidden"
              id="upload-file"
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(file);
                }
              }}
            />
            {isParsing ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100px',
                }}
              >
                <CircularProgress />
              </Box>
            ) : showUploadBox() ? (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <UploadFileIcon
                    sx={{
                      fontSize: '24px',
                      color: '#2196F3',
                    }}
                  />
                </Box>
                <label htmlFor="upload-file" className="cursor-pointer">
                  <Link component="span" underline="hover">
                    {t('glossary.import.upload.click')}
                  </Link>
                </label>
                {' ' + t('glossary.import.upload.dragDrop')}
                <Typography variant="body2" color="text.secondary">
                  {glossaryType === GlossaryCollectionDtoTypeEnum.External
                    ? t('glossary.import.upload.fileTypes.csv')
                    : t('glossary.import.upload.fileTypes.jsonCsv')}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
                  >
                    <InsertDriveFileIcon sx={{ mr: 3, color: '#2196F3' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant="body2">{fileName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(fileSize)} •{' '}
                        {glossary.importFileParseStatus}
                      </Typography>
                    </Box>
                  </Box>
                  <DeleteIcon
                    sx={{ cursor: 'pointer', color: '#0000008F', ml: 2 }}
                    onClick={handleFileRemove}
                  />
                </Box>
                {glossary.importFileParseStatus ===
                  ImportFileParseStatus.Uploading && (
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress
                      variant="determinate"
                      value={glossary.progress}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
{/* 
        {isUseExternalDb && (
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            <Alert
              severity="info"
              sx={{
                mb: 2,
                display: 'flex',
                width: '60%',
                border: '1px solid #2196f3',
                backgroundColor: '#fff',
                paddingRight: '40px',
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {glossaryType === GlossaryCollectionDtoTypeEnum.External
                    ? t('glossary.import.file.externalUpdate')
                    : t('glossary.import.file.size')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {glossaryType === GlossaryCollectionDtoTypeEnum.External
                    ? t('glossary.import.file.externalDescription')
                    : t('glossary.import.file.sizeDescription', {
                      externalLimit: externalGlossaryItemCountLimit,
                    })}
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t('glossary.import.file.note')}
                </Typography>
                <ul
                  style={{
                    paddingLeft: '16px',
                    listStyleType: 'disc',
                  }}
                >
                  <li>
                    <Typography variant="body2">
                      {t('glossary.import.file.noteDescription')}
                    </Typography>
                  </li>
                </ul>
              </Box>
            </Alert>
          </Box>
        )} */}

        {!isUseExternalDb && parsedItems.length > 0 && (
          <PaginatedTable
            dummyPagination
            columns={columns}
            data={parsedItems}
            selectedItems={[]}
            page={page}
            rowsPerPage={rowsPerPage}
            loading={false}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-start',
          }}
        >
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => handleImportGlossary(isUseExternalDb)}
            disabled={
              !name ||
              nameIsProtected ||
              !fileName ||
              glossary.importFileParseStatus === ImportFileParseStatus.Error
            }
            sx={{ width: '99px' }}
            loading={glossary.loading}
          >
            {glossaryUuid
              ? t('glossary.import.buttons.update')
              : t('glossary.import.buttons.import')}
          </LoadingButton>
          <Button onClick={handleCancel} sx={{ ml: 1, width: '99px' }}>
            {t('glossary.import.buttons.cancel')}
          </Button>
        </Box>
      </PaperCard>

      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{t('glossary.import.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('glossary.import.dialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.proceed()}>
            {t('glossary.import.dialog.leave')}
          </Button>
          <Button
            onClick={() => blocker.reset()}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t('glossary.import.dialog.stay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
